import { ChangeDetectorRef, Component, Inject, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MODEL_CONFIG } from './model-config';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { FileInputComponent } from '../../shared/input-file/input-file.component';
import { QuillEditorComponent } from 'ngx-quill';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { RichTextEditorComponent } from '../../shared/rich-text-editor/rich-text-editor.component';
import { ApiService } from '../../services/api.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { error } from '@angular/compiler-cli/src/transformers/util';
import { AvatarUploadComponent } from '../../shared/avatar-upload/avatar-upload.component';


export interface ModelFormDialogData {
  modelType: string;
  action: 'add' | 'update';
  model?: any;
  initData?: any;
}

@Component({
  selector: 'app-model-form-dialog',
  templateUrl: './model-form-dialog.component.html',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDialogActions,
    MatButtonModule,
    NgSwitch,
    NgForOf,
    MatInputModule,
    NgSwitchCase,
    NgIf,
    MatSelectModule,
    MatIconModule,
    FileInputComponent,
    QuillEditorComponent,
    EditorComponent,
    FormsModule,
    RichTextEditorComponent,
    TranslatePipe,
    AvatarUploadComponent
  ],
  styleUrls: ['./model-form-dialog.component.scss']
})
export class ModelFormDialogComponent implements OnInit {
  form!: FormGroup;
  fields: any[] = [];
  title = '';
  service: any;

  content = '';

  tinyConfig = {
    height: 400,
    menubar: false,
    plugins: 'code',
    toolbar: 'insertYoutube insertIframe | bold italic underline | code',
    setup: (editor: any) => {
      // --- 🎥 YouTube Embed Button ---
      editor.ui.registry.addButton('insertYoutube', {
        text: '🎥 YouTube',
        onAction: () => {
          editor.windowManager.open({
            title: 'Embed YouTube Video',
            body: {
              type: 'panel',
              items: [
                {
                  type: 'input',
                  name: 'url',
                  label: 'YouTube URL',
                  placeholder: 'https://www.youtube.com/watch?v=xxxxxx'
                }
              ]
            },
            buttons: [
              { type: 'cancel', text: 'Cancel' },
              { type: 'submit', text: 'Insert', primary: true }
            ],
            onSubmit: (api: any) => {
              const data = api.getData();
              const youtubeUrl = data.url;
              const videoId = this.extractYoutubeId(youtubeUrl);

              if (videoId) {
                const embedHtml = `<iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/${videoId}"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen>
                </iframe>`;
                editor.insertContent(embedHtml);
              } else {
                editor.windowManager.alert('Invalid YouTube URL');
              }
              api.close();
            }
          });
        }
      });

      // --- 🌐 Generic Iframe Embed Button ---
      editor.ui.registry.addButton('insertIframe', {
        text: '🌍 Embed Iframe',
        onAction: () => {
          editor.windowManager.open({
            title: 'Embed Iframe',
            body: {
              type: 'panel',
              items: [
                {
                  type: 'input',
                  name: 'url',
                  label: 'Iframe URL',
                  placeholder: 'https://example.com or Canva site URL'
                },
                {
                  type: 'input',
                  name: 'width',
                  label: 'Width (px or %)',
                  placeholder: 'e.g., 600 or 100%'
                },
                {
                  type: 'input',
                  name: 'height',
                  label: 'Height (px)',
                  placeholder: 'e.g., 400'
                }
              ]
            },
            buttons: [
              { type: 'cancel', text: 'Cancel' },
              { type: 'submit', text: 'Insert', primary: true }
            ],
            onSubmit: (api: any) => {
              const data = api.getData();
              const url = data.url.trim();
              const width = data.width || '100%';
              const height = data.height || '400';

              if (url.startsWith('http')) {
                const embedHtml = `<iframe
                  src="${url}"
                  width="${width}"
                  height="${height}"
                  style="border:0;"
                  allowfullscreen
                  loading="lazy">
                </iframe>`;
                editor.insertContent(embedHtml);
              } else {
                editor.windowManager.alert('Invalid URL');
              }
              api.close();
            }
          });
        }
      });
    }
  };

  // Helper function for YouTube
  extractYoutubeId(url: string): string | null {
    const regExp =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  // Optional: Sanitize output before displaying

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ModelFormDialogComponent>,
    private injector: Injector,
    private apiService: ApiService,
    public translate: TranslateService,
    private snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: ModelFormDialogData
  ) {}

  getFieldValue(key: string, value: any) {
    const item = this.data.initData!.find(
      (d: any) => (d.key == key) !== undefined && d[key] !== null
    );
    if (value['type'] == 'select' && item != null) {
      value.value = { label: item.value.label, value: item.value };
      value.options.push({ label: item.value.label, value: item.value });
    }
    if (value['type'] == 'text' && item != null) {
      value.value = '';
    }
    return value;
  }
  ngOnInit(): void {
    const type = this.data.modelType.toLowerCase();
    const config = MODEL_CONFIG[type];
    this.service = config.service;

    if (!config) throw new Error(`No model config found for ${type}`);

    this.fields = Object.entries(config.fields).map(([key, value]) => {
      return {
        key,
        value
      };
    });
    const formGroupConfig: any = {};
    for (const field of this.fields) {
      const validators = [...(field.value.validators || [])];

      if (field.value.pattern) {
        validators.push(Validators.pattern(field.value.pattern));
      }

      formGroupConfig[field.key] = [
        this.data.model?.[field.key] || '',
        validators
      ];
    }

    this.form = this.fb.group(formGroupConfig);
    this.initFormWithData();
    this.title = `${this.data.action === 'add' ? 'Add' : 'Update'} ${this.data.modelType}`;
  }

  initFormWithData(): void {
    setTimeout(() => {
      this.data.initData.forEach((res: any) => {
        // 1️⃣ Find the field object for this key
        const field = this.fields.find((f: any) => f.key === res.key);

        if (!field) return;

        // 2️⃣ PUSH the options first (just reassign so Angular detects change)
        if (field.value?.type === 'select') {
          const newOption = {
            label: res.value.label,
            value: res.value.id
          };
          if (
            !field.value.options.some(
              (opt: any) => opt.value === newOption.value
            )
          ) {
            field.value.options = [...field.value.options, newOption];
          }
        }

        // 3️⃣ Convert object { value: 'x' } → 'x'
        let actualValue = res.value;
        if (
          typeof actualValue === 'object' &&
          actualValue?.value &&
          typeof actualValue.value === 'object'
        ) {
          actualValue = actualValue.value.id;
        }

        // 4️⃣ Set the form value AFTER options exist
        this.form.get(res.key)?.setValue(actualValue.id);
      });
    }, 0);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSelectOpened(event: any, field: { key: string; value: any }) {
    if (field.value['type'] != 'select' || field.value['fetchUrl'] == null)
      return;

    const pathUrl = field.value['fetchUrl'];
    this.apiService.get(pathUrl).subscribe((res: any) => {
      const resultArr = res['results'].map((r: any) => {
        return { label: r.label, value: r.id, icon: null };
      });
      field.value.options = [];
      field.value.options.push(...resultArr);
    });
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 500);
  }

  onSave(): void {
    const type = this.data.modelType.toLowerCase();
    const config = MODEL_CONFIG[type];
    const serviceClass = config.service;

    if (!serviceClass) {
      console.error(`No service defined for model: ${type}`);
      return;
    }
    const serviceInstance = this.injector.get(serviceClass);
    if (this.data.model?.id == null) {
      if (this.form.valid) {
        serviceInstance.create(this.form.value).subscribe({
          next: (res: any) => {
            if (res['status'] == 201) {
              this.snackbar.open(
                this.translate.instant(config.successMSG.create),
                this.translate.instant('THANKS'),
                { duration: 10000 }
              );
            } else {
              this.snackbar.open(
                this.translate.instant(config.errorMSG.create) +
                  ' ' +
                  res.error.label.join(','),
                this.translate.instant('SORRY'),
                { duration: 10000, panelClass: ['snackbar-error'] }
              );
            }
          },
          error: (err: any) => {
            this.snackbar.open(
              this.translate.instant(config.errorMSG.create) +
                ' ' +
                err.error.label.join(','),
              this.translate.instant('SORRY'),
              { duration: 10000, panelClass: ['snackbar-error'] }
            );
          }
        });
        this.dialogRef.close(this.form.value);
      }
    } else {
      serviceInstance.update(this.form.value, this.data.model.id).subscribe({
        next: (res: any) => {
          if (res['status'] == 200) {
            this.snackbar.open(
              this.translate.instant(config.successMSG.update),
              this.translate.instant('THANKS'),
              { duration: 10000 }
            );
          } else {
            this.snackbar.open(
              this.translate.instant(config.errorMSG.update) +
                ' ' +
                res.error.label.join(','),
              this.translate.instant('SORRY'),
              { duration: 10000, panelClass: ['snackbar-error'] }
            );
          }
        },
        error: (res: any) => {
          this.snackbar.open(
            this.translate.instant(config.errorMSG.update) +
              ' ' +
              res.error.label.join(','),
            this.translate.instant('SORRY'),
            { duration: 10000, panelClass: ['snackbar-error'] }
          );
        }
      });
      this.dialogRef.close(this.form.value);
    }
  }

  handleFiles(files: FileList) {
    this.form.patchValue({ image: files });
  }
}
